import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { usePromoteChannels } from '../hooks/usePromoteChannels';
import { useConnectReturn } from '../hooks/useConnectReturn';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { defaultSocialDays } from '../socialPeriod';
import { SocialLoginVariant, SocialPlatform } from '../types';
import { ChannelCard } from './ChannelCard';
import { ConnectResultAlert } from './ConnectResultAlert';
import { ConnectTargetModal } from './ConnectTargetModal';
import { SoundCloudConnectAlert } from './SoundCloudConnectAlert';
import { useSoundCloudChannel } from './useSoundCloudChannel';
import { ChannelsDataSection } from './ChannelsDataSection';
import { ChannelsSectionHead } from './ChannelsSectionHead';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './PromoteChannels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelsBodyProps {
  accountId: number;
}

/**
 * The social accounts: the connect result, the channel cards with their numbers and posting
 * defaults, the read note and the data section. The Channels page (`PromoteChannelsScreen`) and
 * Settings → Connections draw it. The OAuth return lands on the Channels address
 * (`/app/promote/promoteChannels?promoConnect=&result=`), and `useConnectReturn` reads it here.
 */
const ChannelsBodyComponent: React.FC<ChannelsBodyProps> = ({ accountId }) => {
  const intl = useIntl();
  const {
    channels,
    isLoading,
    error,
    retry,
    saveHandle,
    disconnect,
    connect,
    selectTarget,
    disconnectConnection,
    isWriting,
    writeError,
  } = usePromoteChannels({ accountId });

  // The growth line covers 28 days, and 7 days on a plan with a shorter history (Free).
  const { historyDaysLimit } = useMusic24Capabilities();
  const periodDays = defaultSocialDays(historyDaysLimit);

  const { outcome, dismiss } = useConnectReturn({ onReturn: retry });
  const soundCloud = useSoundCloudChannel({ accountId });
  const [targetPlatform, setTargetPlatform] = useState<SocialPlatform>();

  // What the server's own `M24_LIMIT_SOCIAL` check charges against: a channel holding a platform
  // identity, not a handle-only one.
  const connectedCount = useMemo(
    () => channels.filter((channel) => channel.status === 'connected' || channel.status === 'pendingApproval').length,
    [channels]
  );

  // A login that reached several pages ends here: the artist picks one before
  // the channel is written at all.
  useEffect(() => {
    if (outcome?.result === 'pickTarget') {
      setTargetPlatform(outcome.platform);
    }
  }, [outcome]);

  const closeTargetModal = useCallback(() => setTargetPlatform(undefined), []);

  const onSaveHandle = useCallback(
    (platform: SocialPlatform, handle: string) => {
      saveHandle(platform, handle).catch(console.error);
    },
    [saveHandle]
  );

  const onRemoveHandle = useCallback(
    (platform: SocialPlatform) => {
      disconnect(platform).catch(console.error);
    },
    [disconnect]
  );

  const onConnect = useCallback(
    (platform: SocialPlatform, loginVariant?: SocialLoginVariant) => {
      setTargetPlatform(undefined);
      connect(platform, loginVariant).catch(console.error);
    },
    [connect]
  );

  // The server can start the sign-in of Instagram itself. The result banner offers it when the
  // login through a Facebook Page found no Instagram account.
  const offersInstagramLogin = useMemo(
    () => channels.some((channel) => channel.platform === 'instagram' && channel.alternativeLogin === 'instagram'),
    [channels]
  );

  const onDisconnectConnection = useCallback(
    (platform: SocialPlatform) => {
      disconnectConnection(platform).catch(console.error);
    },
    [disconnectConnection]
  );

  const onSelectTarget = useCallback(
    (platform: SocialPlatform, externalId: string) => {
      selectTarget(platform, externalId)
        .then((saved) => (saved ? setTargetPlatform(undefined) : undefined))
        .catch(console.error);
    },
    [selectTarget]
  );

  const cards = useMemo(
    () =>
      channels.map((channel) => (
        <ChannelCard
          key={channel.platform}
          accountId={accountId}
          channel={channel}
          periodDays={periodDays}
          isSaving={isWriting}
          connectedCount={connectedCount}
          onSaveHandle={onSaveHandle}
          onRemoveHandle={onRemoveHandle}
          onConnect={onConnect}
          onDisconnectConnection={onDisconnectConnection}
        />
      )),
    [
      accountId,
      channels,
      connectedCount,
      isWriting,
      onConnect,
      onDisconnectConnection,
      onRemoveHandle,
      onSaveHandle,
      periodDays,
    ]
  );

  return (
    <>
      <SoundCloudConnectAlert accountId={accountId} onReturn={soundCloud.reload} />
      {outcome !== undefined && (
        <ConnectResultAlert
          outcome={outcome}
          offersInstagramLogin={offersInstagramLogin}
          disabled={isWriting}
          onDismiss={dismiss}
          onConnect={onConnect}
        />
      )}
      {writeError !== undefined && <ErrorAlert error={writeError} />}
      {isLoading ? <LoadingView /> : null}
      {!isLoading && error !== undefined ? <ErrorView error={error} retry={retry} /> : null}
      {!isLoading && error === undefined ? (
        <>
          <ChannelsSectionHead
            title={intl.formatMessage({
              id: 'app.sml.promote.channels.social.title',
              defaultMessage: 'Social channels',
            })}
            subtitle={intl.formatMessage({
              id: 'app.sml.promote.channels.social.subtitle',
              defaultMessage: 'The places where your posts go.',
            })}
          />
          <div className={styles.grid}>{cards}</div>
          <div className={styles.readNote}>
            <FontAwesomeIcon icon={faCircleInfo} className={styles.readNoteIcon} />
            <p className={styles.readNoteText}>
              {withBrandWord(
                intl.formatMessage({
                  id: 'app.sml.promote.channels.readNote',
                  defaultMessage:
                    'MUSIC24 reads each connected channel once a day, and once when you connect it. The best slot is the hour that platform usually rewards, not a measurement of your audience.',
                })
              )}
            </p>
          </div>
          <ChannelsDataSection accountId={accountId} />
        </>
      ) : null}

      <ConnectTargetModal
        accountId={accountId}
        platform={targetPlatform}
        isSaving={isWriting}
        closeModal={closeTargetModal}
        onSelect={onSelectTarget}
        onStartAgain={onConnect}
      />
    </>
  );
};

export const ChannelsBody = TypedMemo(ChannelsBodyComponent);
